import { t } from "@lingui/macro";
import { Aperture, Trash, UploadSimple, Camera, ImageSquare } from "@phosphor-icons/react";
import {
  Avatar,
  AvatarImage,
  buttonVariants,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";

import { useUploadImage } from "@/client/services/storage";
import { useResumeStore } from "@/client/stores/resume";
import { getOrientedFile } from "@/client/utils/image";
import { PictureOptions } from "./options";

export const PictureSection = () => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage } = useUploadImage();

  const setValue = useResumeStore((state) => state.setValue);
  const picture = useResumeStore((state) => state.resume.data.basics.picture);
  const cdnBaseURL = import.meta.env.VITE_CDN_BASE_URL;

  const rewardsValue = useResumeStore((state) => state.resume.data.basics.rewardPoints);

  const [showOptions, setShowOptions] = useState(false);

  const isValidUrl = useMemo(() => z.string().url().safeParse(picture.url).success, [picture.url]);

  const onSelectImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      try {
        let file = event.target.files[0];
        file = await getOrientedFile(file);

        const response = await uploadImage(file);
        const url: any = response.data;

        setValue("basics.picture.url", url);
      } catch (err) {
        console.error("Image processing/upload failed:", err);
      }
    }
  };

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const onAvatarClick = () => {
    if (isValidUrl) {
      setValue("basics.picture.url", "");
      setValue("basics.picture.base64_url", "");
    } else {
      if (isMobile) {
        // Mobile → show Camera / Gallery options
        setShowOptions((prev) => !prev);
      } else {
        // Desktop → directly open file picker
        galleryInputRef.current?.click();
      }
    }
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
    setShowOptions(false);
  };

  const openGallery = () => {
    galleryInputRef.current?.click();
    setShowOptions(false);
  };

  return (
    <div className="flex items-center gap-x-4 relative">
      <div className="group relative cursor-pointer" onClick={onAvatarClick}>
        <Avatar className="size-14 bg-secondary">
          <AvatarImage src={picture.url} />
        </Avatar>

        {isValidUrl ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
            <Trash size={16} weight="bold" />
          </div>
        ) : (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
            <UploadSimple size={16} weight="bold" />
          </div>
        )}

        {/* Mobile-only options */}
        {showOptions && !isValidUrl && isMobile && (
          <div className="absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 flex flex-col gap-2 rounded bg-background p-2 shadow-lg">
            <button
              onClick={openCamera}
              className="flex items-center justify-center gap-x-2 rounded bg-white px-3 py-1 text-sm"
            >
              <Camera size={16} /> Take Photo
            </button>
            <button
              onClick={openGallery}
              className="flex items-center justify-center gap-x-2 rounded bg-white px-3 py-1 text-sm"
            >
              <ImageSquare size={16} /> Choose from Gallery
            </button>
          </div>
        )}
      </div>

      <div className="flex w-full flex-col gap-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="basics.picture.url">{t`Picture`}</Label>
          <span className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <div className="relative group">
              <img
                src={`${cdnBaseURL}/mybharat/assets/img/yuva_landing/profile_reward.png`}
                alt="Reward Points"
                className="rounded-sm"
              />
              {/* Tooltip */}
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 text-xs text-black bg-white rounded opacity-0 group-hover:opacity-100 transition">
                Rewards
              </span>
            </div>
            {rewardsValue ?? 0}
          </span>
        </div>
        <div className="flex items-center gap-x-2">
          {/* Hidden inputs */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={onSelectImage}
            style={{ display: "none" }}
          />
          <input
            type="file"
            accept="image/*"
            ref={galleryInputRef}
            onChange={onSelectImage}
            style={{ display: "none" }}
          />

          <Input
            style={{ display: "none" }}
            id="basics.picture.url"
            placeholder="https://..."
            value={picture.url}
            onChange={(event) => setValue("basics.picture.url", event.target.value)}
          />

          {isValidUrl && (
            <Popover>
              <PopoverTrigger asChild>
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  id="abc"
                  className={cn(buttonVariants({ size: "icon", variant: "ghost" }))}
                >
                  <Aperture />
                </motion.button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px]">
                <PictureOptions />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
};
